import { useDispatch, useSelector } from "react-redux";
import {
  AgeChip,
  EmptyState,
  ErrorBanner,
  Muted,
  RatingChip,
  RowMeta,
  RowName,
  RowWants,
  QueueRow,
} from "../IntakeReviewStyled";
import { openIntakeReviewCard } from "../../../slice/IntakeReviewSlice";
import { ageLabel, ratingChipText, ratingTone } from "../utils";

const EMPTY_COPY = {
  pending: "Nothing waiting on a decision.",
  decided: "No decisions recorded yet.",
  mine: "Nothing is assigned to you.",
};

/**
 * Why the pile is empty.
 *
 * An empty queue has two very different causes and they must never look
 * the same: there is genuinely nothing to decide, or the pull from the
 * case system is failing. The second one hid a broken sync while 69
 * intakes sat in attorney review, so the empty state reports the last
 * pull rather than cheerfully claiming there is no work.
 */
const emptyExplanation = (scope, lastSync) => {
  const base = EMPTY_COPY[scope] || EMPTY_COPY.pending;
  if (scope !== "pending" || !lastSync) return { text: base, failed: false };

  if (lastSync.enabled === false) {
    return {
      text:
        "The live queue is switched off in this environment, so nothing " +
        "has been pulled from the case system.",
      failed: true,
    };
  }
  if (lastSync.errors) {
    return {
      text:
        "Could not read the intake queue from the case system" +
        (lastSync.error ? `: ${lastSync.error}` : "") +
        ". Nothing here is up to date.",
      failed: true,
    };
  }
  return { text: base, failed: false };
};

/**
 * The pile.
 *
 * Deliberately four columns and no more: who, what rating, what they
 * want, how long it has waited. Litify's equivalent screen is a card deck
 * with no list at all — the attorney is handed one intake at a time and
 * cannot see the shape of the queue or choose what to work on, which is
 * why "park it and come back" had to exist as a button.
 */
const QueueList = () => {
  const dispatch = useDispatch();
  const { queue, queueLoading, queueError, scope, lastSync } = useSelector(
    (state) => state.intakeReviews
  );

  if (queueLoading && !queue.items.length) {
    return <Muted>Loading the queue…</Muted>;
  }

  if (queueError) {
    return <ErrorBanner role="alert">{queueError}</ErrorBanner>;
  }

  if (!queue.items.length) {
    const { text, failed } = emptyExplanation(scope, lastSync);
    return failed ? (
      <ErrorBanner role="alert">{text}</ErrorBanner>
    ) : (
      <EmptyState>{text}</EmptyState>
    );
  }

  return (
    <div>
      {queue.items.map((row) => (
        <QueueRow
          key={row.id}
          type="button"
          onClick={() => dispatch(openIntakeReviewCard(row.id))}
        >
          <RowName>{row.clientName || "Unnamed intake"}</RowName>
          <RatingChip $tone={ratingTone(row.ratingBand)}>
            {ratingChipText({
              band: row.ratingBand,
              display: row.ratingDisplay,
            })}
          </RatingChip>
          <RowWants title={row.wants}>
            {row.wants || "No appeal reason recorded"}
          </RowWants>
          <RowMeta>
            {scope === "decided" ? (
              row.statusLabel
            ) : (
              <AgeChip $tone={row.ageTone}>
                {ageLabel(row.ageDays)}
              </AgeChip>
            )}
            {row.assignedTo ? ` · ${row.assignedTo.name}` : ""}
          </RowMeta>
        </QueueRow>
      ))}
    </div>
  );
};

export default QueueList;
